'use client';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { CaretRight } from 'phosphor-icons';
import { useMemo, useState, type JSX } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { CheckboxWithLabel } from '../CheckboxWithLabel';
import type {
  TreeAccordionItemProps,
  TreeAccordionProps,
  TreeNodeData,
} from './TreeAccordion.types';

const TREE_ITEM_BASE_STYLES = c(
  'flex w-full items-center justify-between py-1.5 px-2 gap-3',
  'transition-colors duration-150',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-hover'
);

const TREE_ITEM_CONTENT_STYLES =
  'flex min-w-0 items-center gap-3 overflow-hidden';

const INDENT_PX = 16;

function getSelectedStyles(
  isSelected: boolean,
  isDark: boolean
): {
  text: string;
  count: string;
} {
  if (isSelected) {
    return {
      text: isDark
        ? 'text-sm font-medium text-white'
        : 'text-sm font-medium text-[#141414]',
      count: isDark ? 'text-grey-400' : 'text-grey-500',
    };
  }
  return {
    text: isDark ? 'text-sm text-grey-200' : 'text-sm text-grey-800',
    count: isDark ? 'text-grey-400' : 'text-grey-500',
  };
}

type TreeItemCountProps = {
  count: number;
  isSelected: boolean;
  isDarkTheme: boolean;
};

function TreeItemCount({
  count,
  isSelected,
  isDarkTheme,
}: TreeItemCountProps): JSX.Element {
  const styles = getSelectedStyles(isSelected, isDarkTheme);
  return (
    <span
      className={c(
        'flex shrink-0 items-center justify-start gap-2 overflow-hidden rounded px-1 text-xs',
        isDarkTheme ? 'bg-grey-800' : 'bg-grey-100',
        styles.count
      )}
    >
      {count.toLocaleString()}
    </span>
  );
}

/** Invisible placeholder that reserves horizontal space for the expand caret on leaf rows,
 *  keeping count badges aligned with expandable sibling rows in vertical contexts. */
function CaretSpacer({ className }: { className?: string }): JSX.Element {
  return (
    <div
      data-test="tree-accordion-caret-spacer"
      className={c(
        'pointer-events-none flex shrink-0 items-center justify-center opacity-0',
        className
      )}
      aria-hidden="true"
    >
      <CaretRight size={16} />
    </div>
  );
}

function MultiSelectCountBadge({
  count,
  isDarkTheme,
}: {
  count: number;
  isDarkTheme: boolean;
}): JSX.Element {
  return (
    <span
      className={c(
        'rounded px-1 text-xs leading-5 tracking-tight',
        isDarkTheme ? 'bg-grey-800 text-grey-400' : 'bg-grey-100 text-grey-700'
      )}
    >
      {count.toLocaleString()}
    </span>
  );
}

/** Collect all non-disabled IDs under a node (disabled nodes keep their current state during cascade) */
function collectAllIds<T>(node: TreeNodeData<T>): string[] {
  if (node.disabled) return [];
  const ids: string[] = [node.id];
  if (node.children) {
    for (const child of node.children) {
      ids.push(...collectAllIds(child));
    }
  }
  return ids;
}

/** Compute check state for a parent node: true (all selected), 'indeterminate' (some), false (none).
 *  Disabled nodes are excluded from the computation — they don't affect parent state. */
function getCheckState<T>(
  node: TreeNodeData<T>,
  selectedSet: Set<string>
): boolean | 'indeterminate' {
  if (!node.children || node.children.length === 0) {
    return selectedSet.has(node.id);
  }

  const enabledChildren = node.children.filter((child) => !child.disabled);

  // If all children are disabled, derive state from the node itself
  if (enabledChildren.length === 0) {
    return selectedSet.has(node.id);
  }

  const childStates = enabledChildren.map((child) =>
    getCheckState(child, selectedSet)
  );

  const allChecked = childStates.every((s) => s === true);
  const noneChecked = childStates.every((s) => s === false);

  if (allChecked) return true;
  if (noneChecked) return false;
  return 'indeterminate';
}

/** Pre-compute check states for all nodes in a tree to avoid O(N^2) recomputation.
 *  Returns a Map of node id -> check state (true | false | 'indeterminate'). */
function buildCheckStateMap<T>(
  nodes: TreeNodeData<T>[],
  selectedSet: Set<string>
): Map<string, boolean | 'indeterminate'> {
  const map = new Map<string, boolean | 'indeterminate'>();

  function traverse(node: TreeNodeData<T>): void {
    map.set(node.id, getCheckState(node, selectedSet));
    node.children?.forEach(traverse);
  }

  nodes.forEach(traverse);
  return map;
}

function TreeAccordionItem<T = unknown>({
  item,
  level = 0,
  selectionMode = 'single',
  selectedId,
  selectedIds,
  checkStateMap,
  onSelect,
  onSelectMany,
  className,
  isDarkTheme = false,
  size = 'sm',
  reserveExpandSpace = true,
}: TreeAccordionItemProps<T>): JSX.Element {
  const hasChildren = item.children && item.children.length > 0;
  const isMultiple = selectionMode === 'multiple';

  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

  const isSelected = isMultiple
    ? checkStateMap
      ? checkStateMap.get(item.id) === true
      : selectedSet.has(item.id)
    : selectedId === item.id;

  // Use precomputed check state from map if available, otherwise compute locally
  const checkState = useMemo(() => {
    if (!isMultiple) return isSelected;
    if (checkStateMap) return checkStateMap.get(item.id) ?? false;
    if (!hasChildren) return selectedSet.has(item.id);
    return getCheckState(item, selectedSet);
  }, [isMultiple, isSelected, hasChildren, item, selectedSet, checkStateMap]);

  const isRootLevel = level === 0;
  const basePadding = level * INDENT_PX;
  const styles = getSelectedStyles(
    isMultiple ? checkState === true : isSelected,
    isDarkTheme
  );
  const isDisabled = item.disabled ?? false;

  const showIcon = item.icon && isRootLevel;

  const handleMultiSelectClick = () => {
    if (isDisabled) return;
    if (hasChildren && onSelectMany) {
      const allDescendantIds = collectAllIds(item);
      const shouldSelect = checkState !== true;
      onSelectMany(allDescendantIds, shouldSelect);
    } else if (onSelectMany) {
      onSelectMany([item.id], !isSelected);
    } else {
      onSelect?.(item.id);
    }
  };

  if (!hasChildren) {
    const leafPadding = basePadding;

    if (isMultiple) {
      return (
        <div
          data-slot="tree-accordion-item"
          data-test="tree-accordion-item"
          className={c(
            'flex w-full items-center rounded-lg',
            !isDisabled &&
              (isDarkTheme ? 'hover:bg-white/5' : 'hover:bg-grey-100'),
            className
          )}
        >
          <CheckboxWithLabel
            id={`tree-${item.id}`}
            checked={isSelected}
            disabled={isDisabled}
            isDarkTheme={isDarkTheme}
            onCheckedChange={handleMultiSelectClick}
            role="treeitem"
            aria-level={level + 1}
            aria-selected={isSelected}
            size={size}
            className="min-h-9 flex-1 hover:bg-transparent"
            style={{ paddingLeft: `${leafPadding + 8}px` }}
            onClick={
              !isDisabled
                ? (e) => {
                    if (e.target === e.currentTarget) handleMultiSelectClick();
                  }
                : undefined
            }
          >
            {showIcon && item.icon}
            <span className="flex-1 truncate">{item.label}</span>
            {item.count !== undefined && (
              <MultiSelectCountBadge
                count={item.count}
                isDarkTheme={isDarkTheme}
              />
            )}
          </CheckboxWithLabel>
          {reserveExpandSpace && <CaretSpacer className="py-1 pl-1 pr-3" />}
        </div>
      );
    }

    return (
      <button
        data-slot="tree-accordion-item"
        data-test="tree-accordion-item"
        onClick={() => !isDisabled && onSelect?.(item.id)}
        disabled={isDisabled}
        className={c(
          TREE_ITEM_BASE_STYLES,
          'rounded-lg px-2',
          isSelected && (isDarkTheme ? 'bg-white/5' : 'bg-grey-100'),
          isDisabled && 'cursor-not-allowed opacity-50',
          !isDisabled &&
            (isDarkTheme ? 'hover:bg-white/5' : 'hover:bg-grey-100'),
          className
        )}
        role="treeitem"
        aria-level={level + 1}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
      >
        <div
          className={TREE_ITEM_CONTENT_STYLES}
          style={{ paddingLeft: `${leafPadding}px` }}
        >
          {showIcon && item.icon}
          <span className={c('truncate', styles.text)}>{item.label}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {item.count !== undefined && (
            <TreeItemCount
              count={item.count}
              isSelected={isSelected}
              isDarkTheme={isDarkTheme}
            />
          )}
          {reserveExpandSpace && <CaretSpacer className="p-1" />}
        </div>
      </button>
    );
  }

  return (
    <AccordionPrimitive.Item
      data-slot="tree-accordion-item"
      data-test="tree-accordion-item"
      value={item.id}
      className={c('flex flex-col', className)}
      disabled={isDisabled}
    >
      <AccordionPrimitive.Header className="flex">
        <div
          className={c(
            'group flex w-full items-center',
            isMultiple ? 'rounded-lg' : 'rounded-lg px-2',
            isSelected &&
              !isMultiple &&
              (isDarkTheme ? 'bg-white/5' : 'bg-grey-100'),
            !isMultiple && isDisabled && 'cursor-not-allowed opacity-50',
            !isDisabled &&
              (isDarkTheme ? 'hover:bg-white/5' : 'hover:bg-grey-100'),
            'transition-colors duration-150'
          )}
        >
          {isMultiple ? (
            <CheckboxWithLabel
              id={`tree-${item.id}`}
              checked={checkState}
              disabled={isDisabled}
              isDarkTheme={isDarkTheme}
              onCheckedChange={handleMultiSelectClick}
              size={size}
              className="min-h-9 flex-1"
              style={{ paddingLeft: `${basePadding + 8}px` }}
              onClick={
                !isDisabled
                  ? (e) => {
                      if (e.target === e.currentTarget)
                        handleMultiSelectClick();
                    }
                  : undefined
              }
              role="treeitem"
              aria-level={level + 1}
              aria-selected={checkState === true}
            >
              {showIcon && item.icon}
              <span className="flex-1 truncate">{item.label}</span>
              {item.count !== undefined && (
                <MultiSelectCountBadge
                  count={item.count}
                  isDarkTheme={isDarkTheme}
                />
              )}
            </CheckboxWithLabel>
          ) : (
            <button
              type="button"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                if (!isDisabled) {
                  onSelect?.(item.id);
                }
              }}
              className={c(
                TREE_ITEM_BASE_STYLES,
                'flex min-w-0 flex-1 items-center justify-between gap-2 overflow-hidden',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover'
              )}
              role="treeitem"
              aria-level={level + 1}
              aria-selected={isSelected}
              aria-disabled={isDisabled}
            >
              <div className={TREE_ITEM_CONTENT_STYLES}>
                {showIcon && item.icon}
                <span className={c('truncate', styles.text)}>{item.label}</span>
              </div>
              {item.count !== undefined && (
                <TreeItemCount
                  count={item.count}
                  isSelected={isSelected}
                  isDarkTheme={isDarkTheme}
                />
              )}
            </button>
          )}

          <AccordionPrimitive.Trigger
            data-test="tree-accordion-caret"
            disabled={isDisabled}
            onClick={(e) => e.stopPropagation()}
            className={c(
              'group/caret flex shrink-0 items-center justify-center rounded p-1',
              isMultiple && 'mr-2',
              'hover:bg-grey-200',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-hover'
            )}
          >
            <CaretRight
              size={16}
              className="shrink-0 text-grey-600 transition-transform duration-200 group-data-[state=open]/caret:rotate-90"
            />
          </AccordionPrimitive.Trigger>
        </div>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
        <div className="flex flex-col gap-1">
          {item.children?.map((child) => (
            <TreeAccordionItem
              key={child.id}
              item={child}
              level={level + 1}
              selectionMode={selectionMode}
              selectedId={selectedId}
              selectedIds={selectedIds}
              checkStateMap={checkStateMap}
              onSelect={onSelect}
              onSelectMany={onSelectMany}
              isDarkTheme={isDarkTheme}
              size={size}
              reserveExpandSpace={reserveExpandSpace}
            />
          ))}
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
}

function TreeAccordion<T = unknown>({
  items,
  selectionMode = 'single',
  selectedId,
  selectedIds,
  expandedIds: controlledExpandedIds,
  defaultExpandedIds = [],
  onSelect,
  onSelectMany,
  onExpandedChange,
  className,
  isDarkTheme = false,
  size = 'sm',
  reserveExpandSpace = true,
}: TreeAccordionProps<T>): JSX.Element {
  const [internalExpandedIds, setInternalExpandedIds] =
    useState<string[]>(defaultExpandedIds);

  // Pre-compute check states for all nodes once per render (O(N) instead of O(N^2))
  const checkStateMap = useMemo(() => {
    if (selectionMode !== 'multiple' || !selectedIds) return undefined;
    const selectedSet = new Set(selectedIds);
    return buildCheckStateMap(items, selectedSet);
  }, [selectionMode, selectedIds, items]);

  // Use controlled value if provided, otherwise use internal state
  const isControlled = controlledExpandedIds !== undefined;
  const expandedIds = isControlled
    ? controlledExpandedIds
    : internalExpandedIds;

  function handleExpandedChange(newExpandedIds: string[]): void {
    if (!isControlled) {
      setInternalExpandedIds(newExpandedIds);
    }
    onExpandedChange?.(newExpandedIds);
  }

  return (
    <AccordionPrimitive.Root
      data-slot="tree-accordion"
      type="multiple"
      value={expandedIds}
      onValueChange={handleExpandedChange}
      className={c('flex w-full flex-col gap-1 overflow-hidden', className)}
      role="tree"
      aria-multiselectable={selectionMode === 'multiple' || undefined}
    >
      {items.map((item) => (
        <TreeAccordionItem
          key={item.id}
          item={item}
          selectionMode={selectionMode}
          selectedId={selectedId}
          selectedIds={selectedIds}
          checkStateMap={checkStateMap}
          onSelect={onSelect}
          onSelectMany={onSelectMany}
          isDarkTheme={isDarkTheme}
          size={size}
          reserveExpandSpace={reserveExpandSpace}
        />
      ))}
    </AccordionPrimitive.Root>
  );
}

export { TreeAccordion, TreeAccordionItem };
